<script setup>
import { computed, reactive, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { useNotificationsHub } from 'dashboard/composables/useNotificationsHub';
import { runCustomAutomations } from 'dashboard/composables/useCustomAutomationRunner';
import { isLabelInCategory } from 'dashboard/constants/labelCategories';
import ContactAPI from 'dashboard/api/contacts';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KanbanContactCard from './KanbanContactCard.vue';
import KanbanAddCardDialog from './KanbanAddCardDialog.vue';
import KanbanContactDialog from './KanbanContactDialog.vue';

const props = defineProps({
  board: { type: Object, required: true },
});

const store = useStore();
const kanbanStore = useKanbanStore();
const { logActivity } = useNotificationsHub();
const { t } = useI18n();
const accountLabels = useMapGetter('labels/getLabels');

const addCardDialog = ref(null);
const contactDialog = ref(null);

// columnId -> array of contacts (reactive so vuedraggable mutations re-render)
const columnsData = reactive({});
const isLoading = ref(false);

const boardLabelTitles = computed(() =>
  props.board.columns.map(c => c.labelTitle).filter(Boolean)
);

const labelColorMap = computed(() => {
  const map = {};
  (accountLabels.value || []).forEach(l => {
    map[l.title] = l.color;
  });
  return map;
});

// Labels already linked to a column on ANY board in the account — a label may
// belong to only one kanban column across all boards.
const labelsUsedGlobally = computed(() => {
  const used = new Set();
  kanbanStore.list.forEach(board => {
    (board.columns || []).forEach(c => {
      if (c.labelTitle) used.add(c.labelTitle);
    });
  });
  return used;
});

// Account labels assignable to kanban columns: contact-scoped (or global)
// labels, since dropping a card applies the label to a contact.
const contactLabels = computed(() =>
  (accountLabels.value || []).filter(l => isLabelInCategory(l, 'contact'))
);

// Labels available to link to a given column: contact labels not already used
// by another column on any board (the column keeps its own current label).
const availableLabelsFor = column => {
  const list = contactLabels.value.filter(
    l => l.title === column.labelTitle || !labelsUsedGlobally.value.has(l.title)
  );
  // Always keep the currently-linked label visible, even if it is no longer
  // contact-scoped, so the select reflects the saved value.
  if (column.labelTitle && !list.some(l => l.title === column.labelTitle)) {
    const current = (accountLabels.value || []).find(
      l => l.title === column.labelTitle
    );
    if (current) list.unshift(current);
  }
  return list;
};

// Labels available when creating a new column (none of them already in use).
const availableNewColumnLabels = computed(() =>
  contactLabels.value.filter(l => !labelsUsedGlobally.value.has(l.title))
);

const fetchColumn = async column => {
  if (!column.labelTitle) {
    columnsData[column.id] = [];
    return;
  }
  try {
    const { data } = await ContactAPI.get(1, 'name', column.labelTitle);
    columnsData[column.id] = data.payload || [];
  } catch {
    columnsData[column.id] = [];
  }
};

const fetchAll = async () => {
  isLoading.value = true;
  await Promise.all(props.board.columns.map(fetchColumn));
  isLoading.value = false;
};

// Apply the label transition when a contact card is dropped into a column.
const applyTransition = async (contact, targetColumn) => {
  try {
    const { data } = await ContactAPI.getContactLabels(contact.id);
    const current = data.payload || [];
    // Remove any of THIS board's status labels, then add the target's label.
    let next = current.filter(l => !boardLabelTitles.value.includes(l));
    if (targetColumn.labelTitle) next.push(targetColumn.labelTitle);
    next = [...new Set(next)];

    await ContactAPI.updateContactLabels(contact.id, next);
    const contactName = contact.name || t('KANBAN.ACTIVITY.DEFAULT_CONTACT_NAME');
    if (targetColumn.labelTitle) {
      logActivity({
        category: 'label',
        action: 'assigned',
        title: t('KANBAN.ACTIVITY.LABEL_ASSIGNED_TITLE', {
          label: targetColumn.labelTitle,
        }),
        body: t('KANBAN.ACTIVITY.LABEL_ASSIGNED_BODY', {
          contact: contactName,
          label: targetColumn.labelTitle,
          board: props.board.name,
        }),
        meta: { contactId: contact.id, label: targetColumn.labelTitle },
      });
    } else {
      logActivity({
        category: 'label',
        action: 'removed',
        title: t('KANBAN.ACTIVITY.LABEL_REMOVED_TITLE'),
        body: t('KANBAN.ACTIVITY.LABEL_REMOVED_BODY', {
          contact: contactName,
          board: props.board.name,
        }),
        meta: { contactId: contact.id },
      });
    }
    useAlert(
      targetColumn.labelTitle
        ? t('KANBAN.ACTIVITY.TOAST_LABEL_ASSIGNED', {
            contact: contactName,
            label: targetColumn.labelTitle,
          })
        : t('KANBAN.ACTIVITY.TOAST_LABEL_REMOVED', { contact: contactName })
    );
    // Let server-side automation rules react to the card movement.
    runCustomAutomations('kanban_card_moved', {
      board: props.board.name,
      to_column: targetColumn.title || '',
      to_label: targetColumn.labelTitle || '',
      contact_ids: [contact.id],
      agent_ids: [],
    });
  } catch {
    useAlert(t('KANBAN.ACTIVITY.UPDATE_ERROR'));
    fetchAll();
  }
};

const onColumnChange = (column, event) => {
  if (event.added) {
    applyTransition(event.added.element, column);
  }
};

const onLinkLabel = async (column, labelTitle) => {
  const color = labelColorMap.value[labelTitle] || null;
  try {
    await kanbanStore.updateColumn(props.board.id, column.id, {
      labelTitle: labelTitle || null,
      labelColor: color,
    });
    fetchColumn({ ...column, labelTitle: labelTitle || null });
  } catch {
    // Backend rejects a label already linked elsewhere — resync + warn.
    useAlert(t('KANBAN.LABEL_IN_USE'));
    kanbanStore.fetch();
  }
};

const openAddCard = column => addCardDialog.value?.openForColumn(column);

const onCardAdded = columnId => {
  const column = props.board.columns.find(c => c.id === columnId);
  if (column) fetchColumn(column);
};

const onSelectContact = contact =>
  contactDialog.value?.openForContact(contact);

// Reflect edited contact data on its card(s) without a full refetch.
const onContactUpdated = updated => {
  Object.keys(columnsData).forEach(columnId => {
    columnsData[columnId] = (columnsData[columnId] || []).map(c =>
      c.id === updated.id ? { ...c, ...updated } : c
    );
  });
};

const removeColumn = column => {
  kanbanStore.removeColumn(props.board.id, column.id);
  delete columnsData[column.id];
};

const onRenameColumn = (column, title) => {
  kanbanStore.updateColumn(props.board.id, column.id, { title });
};

// Add-column inline form
const isAddingColumn = ref(false);
const newColumnTitle = ref('');
const newColumnLabel = ref('');

const submitNewColumn = () => {
  if (!newColumnTitle.value.trim()) return;
  const color = labelColorMap.value[newColumnLabel.value] || null;
  const column = kanbanStore.addColumn(props.board.id, {
    title: newColumnTitle.value,
    labelTitle: newColumnLabel.value || null,
    labelColor: color,
  });
  newColumnTitle.value = '';
  newColumnLabel.value = '';
  isAddingColumn.value = false;
  if (column) fetchColumn(column);
};

const selectClass =
  'h-8 px-2 rounded-md text-xs bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand max-w-[10rem]';

onMounted(async () => {
  store.dispatch('labels/get');
  await fetchAll();
});

// Re-fetch when switching boards.
watch(
  () => props.board.id,
  () => fetchAll()
);
</script>

<template>
  <div class="flex gap-4 h-full overflow-x-auto pb-4 items-start">
    <!-- Columns -->
    <div
      v-for="column in board.columns"
      :key="column.id"
      class="flex flex-col w-72 flex-shrink-0 rounded-xl bg-n-solid-2 border border-n-weak max-h-full"
    >
      <!-- Column header -->
      <div class="flex flex-col gap-2 p-3 border-b border-n-weak">
        <div class="flex items-center justify-between gap-2">
          <input
            :value="column.title"
            class="flex-1 min-w-0 bg-transparent text-sm font-semibold text-n-slate-12 outline-none"
            @change="onRenameColumn(column, $event.target.value)"
          />
          <span class="text-xs text-n-slate-10">
            {{ (columnsData[column.id] || []).length }}
          </span>
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-x"
            @click="removeColumn(column)"
          />
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="column.labelTitle"
            class="size-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: column.labelColor || '#9ca3af' }"
          />
          <select
            :value="column.labelTitle || ''"
            :class="selectClass"
            @change="onLinkLabel(column, $event.target.value)"
          >
            <option value="">{{ t('KANBAN.BOARD.NO_LABEL') }}</option>
            <option
              v-for="label in availableLabelsFor(column)"
              :key="label.id"
              :value="label.title"
            >
              {{ label.title }}
            </option>
          </select>
        </div>
      </div>

      <!-- Cards (draggable) -->
      <Draggable
        :list="columnsData[column.id] || []"
        :group="`kanban-${board.id}`"
        item-key="id"
        tag="div"
        class="flex flex-col gap-2 p-3 overflow-y-auto flex-1 min-h-[6rem]"
        ghost-class="kanban-ghost"
        @change="onColumnChange(column, $event)"
      >
        <template #item="{ element }">
          <KanbanContactCard :contact="element" @select="onSelectContact" />
        </template>
      </Draggable>

      <!-- Add card -->
      <button
        type="button"
        class="flex items-center justify-center gap-1 m-3 mt-0 py-2 text-xs font-medium rounded-lg text-n-slate-11 border border-dashed border-n-weak hover:border-n-strong hover:text-n-slate-12"
        @click="openAddCard(column)"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('KANBAN.ADD_CARD.BUTTON') }}
      </button>
    </div>

    <!-- Add column -->
    <div class="w-72 flex-shrink-0">
      <div
        v-if="isAddingColumn"
        class="flex flex-col gap-2 p-3 rounded-xl bg-n-solid-2 border border-n-weak"
      >
        <Input
          v-model="newColumnTitle"
          :placeholder="t('KANBAN.BOARD.NEW_COLUMN_PLACEHOLDER')"
          autofocus
        />
        <select v-model="newColumnLabel" :class="selectClass + ' max-w-full'">
          <option value="">{{ t('KANBAN.BOARD.NO_LABEL_OPTIONAL') }}</option>
          <option
            v-for="label in availableNewColumnLabels"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
        <div class="flex items-center gap-2">
          <Button
            :label="t('KANBAN.BOARD.ADD')"
            color="blue"
            size="sm"
            @click="submitNewColumn"
          />
          <Button
            :label="t('KANBAN.BOARD.CANCEL')"
            variant="ghost"
            color="slate"
            size="sm"
            @click="isAddingColumn = false"
          />
        </div>
      </div>
      <Button
        v-else
        :label="t('KANBAN.BOARD.ADD_COLUMN')"
        variant="outline"
        icon="i-lucide-plus"
        class="w-full"
        @click="isAddingColumn = true"
      />
    </div>

    <KanbanAddCardDialog ref="addCardDialog" @added="onCardAdded" />
    <KanbanContactDialog ref="contactDialog" @updated="onContactUpdated" />
  </div>
</template>

<style scoped>
.kanban-ghost {
  opacity: 0.4;
}
</style>
